import React, { useState } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TableWrapper from '../utils/TableWrapper';
import Modal from '../components/Modal';
import MainContent from '../components/MainContent';
import useGetUserRole from '../hooks/useGetUserRole';
import Spinner from '../components/spinner/Spinner';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import CohortOnboardingConfig from '../components/CohortOnboardingConfig';
import { isAdminOrAbove } from '../utils/roles';

function OnboardingAutomation() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const navigate = useNavigate();
  const role = useGetUserRole();

  // Fetch cohorts - must be before conditional return for hooks rules
  const { data, isLoading } = useFetchDatav2(
    ['cohorts'],
    '/cohorts',
    {},
    "Couldn't get cohorts. Please try again!",
    true
  );

  // Redirect non-admin users
  if (!isAdminOrAbove(role)) {
    return (
      <MainContent full>
        <div className="bg-white px-6 py-12 rounded-lg text-center">
          <div className="text-claret-500 text-6xl mb-4">
            <LockIcon fontSize="inherit" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            This feature is only available to Administrators and SuperAdmins.
          </p>
          <button
            type="button"
            className="mt-4 bg-claret-500 text-white text-sm py-2 px-6 rounded-md cursor-pointer"
            onClick={() => navigate('/')}
          >
            Go to Dashboard
          </button>
        </div>
      </MainContent>
    );
  }

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const handleConfigureCohort = (cohort) => {
    setSelectedCohort(cohort);
    toggleModal();
  };

  const columns = [
    {
      title: 'Cohort Name',
      field: 'name',
      render: (row) => <p className="font-medium">{row.name}</p>,
    },
    {
      title: 'Students',
      field: 'no_of_students',
      render: (row) => <p>{row.no_of_students || 0}</p>,
    },
    {
      title: 'Start Date',
      field: 'start_date',
      render: (row) => (
        <p>
          {new Date(row?.start_date).toLocaleDateString('en-us', {
            month: 'short',
            year: 'numeric',
            day: 'numeric',
          })}
        </p>
      ),
    },
    {
      title: 'Status',
      field: 'status',
      render: (row) => {
        if (row.status === 'live') {
          return (
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium capitalize">
              {row.status}
            </span>
          );
        }
        if (row.status === 'upcoming') {
          return (
            <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-medium capitalize">
              {row.status}
            </span>
          );
        }
        return (
          <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium capitalize">
            {row.status}
          </span>
        );
      },
    },
    {
      title: 'Onboarding Status',
      field: 'onboarding_status',
      render: (row) => {
        const status = row.onboarding_status || 'NOT_CONFIGURED';
        let colorClass = 'bg-gray-100 text-gray-700';
        let label = 'Not Configured';

        if (status === 'COMPLETED') {
          colorClass = 'bg-green-100 text-green-700';
          label = 'Completed';
        } else if (status === 'SCHEDULED') {
          colorClass = 'bg-blue-100 text-blue-700';
          label = 'Scheduled';
        } else if (status === 'CONFIGURED') {
          colorClass = 'bg-purple-100 text-purple-700';
          label = 'Configured';
        }

        return (
          <span
            className={`${colorClass} px-3 py-1 rounded-full text-xs font-medium`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            className="bg-claret-500 px-3 py-1.5 rounded-md text-white text-xs cursor-pointer hover:bg-claret-600 flex items-center gap-1"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleConfigureCohort(row);
            }}
          >
            <SettingsIcon fontSize="small" />
            Configure
          </button>
          {row.has_onboarding_config && (
            <button
              className="bg-persian-500 px-3 py-1.5 rounded-md text-white text-xs cursor-pointer hover:bg-persian-600 flex items-center gap-1"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleConfigureCohort(row); // Opens modal, user can trigger from there
              }}
              title="View config and trigger automation"
            >
              <PlayArrowIcon fontSize="small" />
              Trigger
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <MainContent full>
        {/* Header */}
        <div className="bg-white px-4 py-5 rounded-lg flex justify-between items-center">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
            <NavLink
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center' }}
              color="inherit"
              to="/"
              className="flex items-center"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Home</p>
            </NavLink>
            <p className="text-gray-500">Onboarding Automation</p>
          </Breadcrumbs>
        </div>

        {/* Info Card */}
        <div className="mt-4 bg-gradient-to-r from-claret-500 to-claret-600 rounded-lg p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="bg-white/20 rounded-lg p-3">
              <SettingsIcon fontSize="large" />
            </div>
            <div>
              <h2 className="text-xl font-semibold mb-1">
                Learner Onboarding Automation
              </h2>
              <p className="text-white/80 text-sm max-w-2xl">
                Configure cohort-specific onboarding settings including
                orientation details, resource URLs, and Google Forms. Once
                configured, the n8n automation will send scheduled emails to
                learners based on their orientation date.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-4 grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
            <p className="text-gray-500 text-sm">Active Cohorts</p>
            <p className="text-2xl font-bold text-gray-800">
              {data?.data?.cohorts?.filter((c) => c.status === 'live').length ||
                0}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border-l-4 border-orange-500">
            <p className="text-gray-500 text-sm">Upcoming Cohorts</p>
            <p className="text-2xl font-bold text-gray-800">
              {data?.data?.cohorts?.filter((c) => c.status === 'upcoming')
                .length || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
            <p className="text-gray-500 text-sm">Total Students</p>
            <p className="text-2xl font-bold text-gray-800">
              {data?.data?.cohorts?.reduce(
                (acc, c) => acc + (c.no_of_students || 0),
                0
              ) || 0}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
            <p className="text-gray-500 text-sm">Automation Status</p>
            <p className="text-lg font-bold text-green-600">Active</p>
          </div>
        </div>

        {/* Table */}
        <div className="mt-4">
          {isLoading ? (
            <div className="bg-white rounded-lg p-8 flex justify-center">
              <Spinner />
            </div>
          ) : (
            <TableWrapper
              title="Cohort Onboarding Configuration"
              columns={columns}
              data={data?.data?.cohorts || []}
              options={{
                rowStyle: {
                  overflowWrap: 'break-word',
                },
              }}
            />
          )}
        </div>
      </MainContent>

      {/* Configuration Modal */}
      <Modal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        title={`Onboarding Configuration - ${selectedCohort?.name || ''}`}
      >
        {selectedCohort && (
          <CohortOnboardingConfig
            cohortId={selectedCohort.id}
            toggleModal={toggleModal}
          />
        )}
      </Modal>
    </>
  );
}

export default OnboardingAutomation;
